import { stringifyGraph } from '@/clients/http'
import { CustomerModel } from '@/http/models/customer'
import { toast } from 'sonner'

import { useState } from 'react'

import { CustomerModel as CustomerModelType } from '@/types'

import { useFetchCustomers } from './useFetchCustomers'

type DialogMode = 'create' | 'edit' | 'view'

export const useCustomersTable = () => {
  // Dialog state
  const [mode, setMode] = useState<DialogMode>('create')
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false)
  const [editCustomer, setEditCustomer] = useState<CustomerModelType.Customer | null>(null)

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [customerToDelete, setCustomerToDelete] = useState<CustomerModelType.Customer | null>(null)

  // Loading states
  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Fetch customers data
  const { loading, data, pagination, formValues, formMethods, setPage, setPageSize, refresh } =
    useFetchCustomers({
      sort: null,
    })

  // Dialog handlers
  const openCreateDialog = () => {
    setMode('create')
    setEditCustomer(null)
    setCustomerDialogOpen(true)
  }

  const openViewDialog = (customer: CustomerModelType.Customer) => {
    setMode('view')
    setEditCustomer(customer)
    setCustomerDialogOpen(true)
  }

  const openEditDialog = async (customer: CustomerModelType.Customer) => {
    setMode('edit')
    setUpdateLoading(true)

    const response = await CustomerModel.getById({
      params: { id: customer._id },
      query: {
        graph_enc: stringifyGraph({
          '*': true,
          roles: {
            '*': true,
          },
          image: {
            path: true,
            versions: true,
          },
        }),
      },
    })

    if (response.success && response.data) {
      setEditCustomer(response.data.result)
      setCustomerDialogOpen(true)
    } else {
      toast.error('Failed to fetch customer details')
    }
    setUpdateLoading(false)
  }

  const closeDialog = () => {
    setCustomerDialogOpen(false)
    setEditCustomer(null)
  }

  // Delete handlers
  const handleDeleteClick = (customer: CustomerModelType.Customer) => {
    setCustomerToDelete(customer)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!customerToDelete?._id) return

    setDeleteLoading(true)
    const response = await CustomerModel.delete({
      params: { id: customerToDelete._id },
    })

    if (response.success) {
      toast.success('Customer deleted successfully')
      setDeleteDialogOpen(false)
      setCustomerToDelete(null)
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to delete customer')
    }
    setDeleteLoading(false)
  }

  // Block toggle handler
  const toggleBlock = async (customer: CustomerModelType.Customer) => {
    const response = await CustomerModel.update({
      params: { id: customer._id },
      body: { is_blocked: !customer.is_blocked },
    })

    if (response.success) {
      toast.success('Customer status updated')
      refresh()
    } else {
      toast.error('Failed to update status')
    }
  }

  // CRUD handlers
  const handleCreateCustomer = async (data: any) => {
    setCreateLoading(true)
    const response = await CustomerModel.create({
      body: data,
    })

    if (response.success) {
      toast.success('Customer created successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to create customer')
    }
    setCreateLoading(false)
  }

  const handleUpdateCustomer = async (id: string, data: any) => {
    setUpdateLoading(true)
    const response = await CustomerModel.update({
      params: { id },
      body: data,
    })

    if (response.success) {
      toast.success('Customer updated successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to update customer')
    }
    setUpdateLoading(false)
  }

  // Computed values
  const stats = {
    totalCustomers: pagination.totalCount,
    activeCustomers: data.filter((c) => c.status === 'active').length,
  }

  return {
    // Data
    loading,
    data,
    pagination,
    formValues,
    formMethods,
    stats,

    // Dialog state
    mode,
    customerDialogOpen,
    editCustomer,

    // Delete dialog state
    deleteDialogOpen,
    customerToDelete,

    // Loading states
    createLoading,
    updateLoading,
    deleteLoading,

    // Pagination handlers
    setPage,
    setPageSize,
    refresh,

    // Dialog handlers
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,

    // Delete handlers
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,

    // CRUD handlers
    handleCreateCustomer,
    handleUpdateCustomer,
    toggleBlock,
  }
}
